import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import {
  collection,
  query,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
  limit
} from 'firebase/firestore'
import { db } from '../config/firebase'
import { useAuth } from '../hooks/useAuth'
import { useCare } from '../hooks/useCare'
import { Medication, MedicationActivity, DailyProgress } from '../types/medication'
import { getSubscriptionLimit } from '../config/subscription'

interface MedicationContextType {
  medications: Medication[]
  todayMedications: Medication[]
  todayActivities: MedicationActivity[] // EXPOSED for optimistic UI
  loading: boolean
  progress: DailyProgress | null
  loadMedications: (options?: { silent?: boolean; skipActivities?: boolean }) => Promise<void>
  loadTodayMedications: () => Promise<void>
  addMedication: (medication: Omit<Medication, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>
  updateMedication: (id: string, medication: Partial<Medication>, options?: { silent?: boolean }) => Promise<void>
  deleteMedication: (id: string) => Promise<void>
  takeMedication: (medicationId: string, scheduledTime?: string) => Promise<void>
  takeMedicationWithStockUpdate: (medicationId: string, scheduledTime?: string, newStock?: number) => Promise<void>
  checkStockBeforeTaking: (medicationId: string, scheduledTime?: string) => Promise<{ shouldShowModal: boolean; medication: Medication | null }>
  adjustStock: (medicationId: string, adjustment: number, reason?: string) => Promise<void>
  calculateTodayProgress: () => Promise<DailyProgress>
  hasTimeTakenToday: (medicationId: string, scheduledTime?: string) => Promise<boolean>
  shouldTakeToday: (medication: Medication) => boolean
  isMedicationActiveOnDate: (medication: Medication, date: Date) => boolean
  shouldTakeOnDate: (medication: Medication, date: Date) => boolean
  playTakeSound: () => void
  getMonthlyHistory: (year: number, month: number) => Promise<MedicationActivity[]>
  getDayActivities: (date: Date) => Promise<MedicationActivity[]>
  getAdherenceStats: (startDate: Date, endDate: Date) => Promise<{
    totalScheduled: number
    totalTaken: number
    totalMissed: number
    adherencePercentage: number
    dailyStats: Array<{
      date: string
      scheduled: number
      taken: number
      percentage: number
    }>
  }>
  getWeeklyStats: () => Promise<Array<{ day: string; taken: number; total: number }>>
  getMonthlyStats: () => Promise<Array<{ week: string; adherence: number }>>
  deleteActivity: (activityId: string) => Promise<void>
  debugActivities: () => Promise<void>

}

const MedicationContext = createContext<MedicationContextType | undefined>(undefined)

export const useMedicationContext = () => {
  const context = useContext(MedicationContext)
  if (context === undefined) {
    throw new Error('useMedicationContext must be used within a MedicationProvider')
  }
  return context
}

interface MedicationProviderProps {
  children: React.ReactNode
}

export const MedicationProvider: React.FC<MedicationProviderProps> = ({ children }) => {
  const [medications, setMedications] = useState<Medication[]>([])
  const [todayMedications, setTodayMedications] = useState<Medication[]>([])
  // Cache for today's activities to avoid repetitive DB calls
  const [todayActivities, setTodayActivities] = useState<MedicationActivity[]>([])
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState<DailyProgress | null>(null)
  const { user, userProfile } = useAuth()
  const { currentCase } = useCare()



  // Add ref to prevent race conditions
  const loadRequestId = useRef(0)
  const activityLoadRequestId = useRef(0)

  // Audio context for take sound
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null)

  useEffect(() => {
    // Initialize audio context
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
      setAudioContext(ctx)
    } catch (e) {
      console.log('Audio not available:', e)
    }
  }, [])

  // Helper function to get medication collection (case-based or user-based)
  const getMedicationCollection = useCallback(() => {
    if (!user?.uid) throw new Error('User not authenticated')

    // If we have a current case, use case-based medications
    if (currentCase?.id) {
      return collection(db, `care-cases/${currentCase.id}/medications`)
    }

    // Fallback to user-based medications for backwards compatibility
    return collection(db, `users/${user.uid}/medications`)
  }, [user?.uid, currentCase?.id])

  // Helper function to get activity collection (case-based or user-based)
  const getActivityCollection = useCallback(() => {
    if (!user?.uid) throw new Error('User not authenticated')

    // If we have a current case, use case-based history
    if (currentCase?.id) {
      return collection(db, `care-cases/${currentCase.id}/history`)
    }

    // Fallback to user-based history for backwards compatibility
    return collection(db, `users/${user.uid}/history`)
  }, [user?.uid, currentCase?.id])

  // Helper function to migrate activities for medications
  const migrateActivitiesToCase = useCallback(async (medicationIds: string[], caseId: string) => {
    if (!user?.uid) return

    try {
      console.log('🔄 Starting activity migration for', medicationIds.length, 'medications')

      // Query all activities for these medications
      const userHistoryRef = collection(db, `users/${user.uid}/history`)
      const activitiesQuery = query(
        userHistoryRef,
        where('medicationId', 'in', medicationIds.slice(0, 10)) // Firestore 'in' limit is 10
      )

      const activitiesSnapshot = await getDocs(activitiesQuery)
      console.log('📋 Found', activitiesSnapshot.docs.length, 'activities to migrate')

      if (activitiesSnapshot.docs.length > 0) {
        const activityBatch = writeBatch(db)

        // Add activities to case collection
        activitiesSnapshot.docs.forEach(activityDoc => {
          const caseHistoryRef = collection(db, `care-cases/${caseId}/history`)
          const newActivityRef = doc(caseHistoryRef)

          const activityData = activityDoc.data()
          activityBatch.set(newActivityRef, {
            ...activityData,
            migratedFromUser: true
          })
        })

        await activityBatch.commit()
        console.log('✅ Migrated', activitiesSnapshot.docs.length, 'activities to case')

        // Mark original activities as migrated (preserve for audit)
        const userActivityBatch = writeBatch(db)
        activitiesSnapshot.docs.forEach(activityDoc => {
          userActivityBatch.update(activityDoc.ref, {
            migratedToCase: caseId,
            migratedAt: serverTimestamp()
          })
        })

        await userActivityBatch.commit()
        console.log('✅ Marked user activities as migrated')
      }

      // Handle remaining medications if there are more than 10
      if (medicationIds.length > 10) {
        const remainingIds = medicationIds.slice(10)
        await migrateActivitiesToCase(remainingIds, caseId)
      }

    } catch (error) {
      console.error('❌ Error migrating activities:', error)
    }
  }, [user?.uid])

  // Helper function to migrate medications from user to case
  const migrateMedicationsToCase = useCallback(async (medications: Medication[], caseId: string) => {
    try {
      console.log('🔄 Starting medication migration to case:', caseId)
      const batch = writeBatch(db)

      // Add medications to case collection
      for (const medication of medications) {
        const caseCollection = collection(db, `care-cases/${caseId}/medications`)
        const newMedicationRef = doc(caseCollection)

        // Remove the id field and add timestamps
        const { id, ...medicationData } = medication
        batch.set(newMedicationRef, {
          ...medicationData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          migratedFromUser: true // Mark as migrated
        })
      }

      // Execute batch write
      await batch.commit()
      console.log('✅ Migrated', medications.length, 'medications to case')

      // Migrate activities for these medications
      await migrateActivitiesToCase(medications.map(m => m.id), caseId)

      // After successful migration, optionally archive user medications
      // Instead of deleting them, we'll mark them as migrated to preserve history
      const userBatch = writeBatch(db)
      for (const medication of medications) {
        const userMedicationRef = doc(db, `users/${user?.uid}/medications`, medication.id)
        userBatch.update(userMedicationRef, {
          migratedToCase: caseId,
          migratedAt: serverTimestamp(),
          isActive: false // Mark as inactive
        })
      }

      await userBatch.commit()
      console.log('✅ Marked user medications as migrated')

    } catch (error) {
      console.error('❌ Error migrating medications:', error)
      throw error
    }
  }, [user?.uid, migrateActivitiesToCase])

  // Load today's activities to cache them
  const loadTodayActivities = useCallback(async () => {
    if (!user?.uid) return

    const requestId = ++activityLoadRequestId.current

    try {
      const today = new Date()
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate())
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)

      console.log('🔄 Loading today activities...', {
        start: startOfDay.toISOString(),
        end: endOfDay.toISOString(),
        requestId
      })

      const historyCollection = getActivityCollection()
      const activityQuery = query(
        historyCollection,
        where('timestamp', '>=', startOfDay),
        where('timestamp', '<', endOfDay),
        orderBy('timestamp', 'desc')
      )

      const snapshot = await getDocs(activityQuery)

      // Check if this request is still valid
      if (requestId !== activityLoadRequestId.current) {
        console.log('⚠️ [loadTodayActivities] Stale request ignored', requestId)
        return
      }

      const activities = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        actualTime: doc.data().actualTime?.toDate() || new Date(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      })) as MedicationActivity[]

      console.log('✅ Loaded', activities.length, 'activities for today')
      setTodayActivities(activities)
    } catch (error) {
      console.error('❌ Error loading today activities:', error)
    }
  }, [user?.uid, getActivityCollection])

  const loadMedications = useCallback(async (options: { silent?: boolean; skipActivities?: boolean } = {}) => {
    if (!user?.uid) return

    const requestId = ++loadRequestId.current

    console.log('🚀 [loadMedications] Starting...', {
      userUid: user.uid,
      caseId: currentCase?.id,
      timestamp: new Date().toISOString(),
      loading: loading,
      requestId
    })

    if (!options.silent) setLoading(true)
    try {
      let medicationList: Medication[] = []

      // If we have a current case, ONLY load from case collection
      if (currentCase?.id) {
        console.log('🔍 Loading medications from case:', currentCase.id)
        const caseCollection = collection(db, `care-cases/${currentCase.id}/medications`)
        const caseQuery = query(caseCollection, orderBy('createdAt', 'desc'))
        const caseSnapshot = await getDocs(caseQuery)

        // Check for stale request immediately after await
        if (requestId !== loadRequestId.current) {
          console.log('⚠️ [loadMedications] Stale request ignored (after fetch)', requestId)
          return
        }

        medicationList = caseSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
          collectionPath: caseCollection.path // Store explicit path
        } as Medication))

        console.log('📋 Case medications found:', medicationList.length)
      } else {
        // Only load user medications if NO case is selected (backwards compatibility)
        console.log('🔍 Loading medications from user (no case selected):', user.uid)
        const userCollection = collection(db, `users/${user.uid}/medications`)
        const userQuery = query(
          userCollection,
          orderBy('createdAt', 'desc')
        )
        const userSnapshot = await getDocs(userQuery)

        // Check for stale request immediately after await
        if (requestId !== loadRequestId.current) {
          console.log('⚠️ [loadMedications] Stale request ignored (after fetch)', requestId)
          return
        }

        const allUserMedications = userSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            updatedAt: doc.data().updatedAt?.toDate() || new Date(),
            collectionPath: userCollection.path // Store explicit path
          } as Medication))

        console.log('📋 Raw user medications found:', allUserMedications.length)

        // Filter out medications that have been migrated to cases
        const userMedications = allUserMedications
          .filter(med =>
            med.isActive !== false &&
            !med.migratedToCase
          )

        medicationList = userMedications
        console.log('📋 Active user medications found:', medicationList.length)
      }

      // CRITICAL: Deduplicate by ID and filter out corrupted medications
      const uniqueMedications = medicationList
        .filter(med => med.id && med.name) // Remove corrupted medications without ID or name
        .reduce((unique: Medication[], current) => {
          // Only add if ID doesn't already exist
          if (!unique.find(med => med.id === current.id)) {
            unique.push(current)
          }
          return unique
        }, [])

      console.log('🧹 After deduplication and cleanup:', uniqueMedications.length, 'unique medications from', medicationList.length, 'total')

      // Final check before state update
      if (requestId !== loadRequestId.current) {
        console.log('⚠️ [loadMedications] Stale request ignored (before state update)', requestId)
        return
      }

      setMedications(uniqueMedications)

      // Load today's activities after loading medications ONLY if not skipped
      if (!options.skipActivities) {
        await loadTodayActivities()
      }

      console.log('✅ [loadMedications] Completed:', uniqueMedications.length, currentCase?.id ? `(case: ${currentCase.id})` : '(user: ' + user.uid + ')', {
        timestamp: new Date().toISOString()
      })

    } catch (error) {
      console.error('❌ [loadMedications] Error:', error)
      throw error
    } finally {
      // Only turn off loading if this was the latest request
      if (requestId === loadRequestId.current) {
        if (!options.silent) setLoading(false)
      }
    }
  }, [user?.uid, currentCase?.id, loadTodayActivities])

  // Helper to check if medication is active on a specific date (not before createdAt)
  const isMedicationActiveOnDate = useCallback((medication: Medication, date: Date): boolean => {
    // Exclude explicitly inactive medications
    if (medication.isActive === false) return false

    // Exclude medications that have been migrated to a case (to avoid duplicates)
    if (medication.migratedToCase) return false

    // Exclude corrupted medications without name
    if (!medication.name) return false

    // Check if date is before medication creation date (start of day comparison)
    if (medication.createdAt) {
      const createdAtDate = medication.createdAt instanceof Date 
        ? medication.createdAt 
        : new Date(medication.createdAt)
      
      // Compare dates at start of day (ignore time)
      const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      const createdAtStart = new Date(createdAtDate.getFullYear(), createdAtDate.getMonth(), createdAtDate.getDate())
      
      if (dateStart < createdAtStart) {
        return false
      }
    }

    return true
  }, [])

  // Helper to check if medication should be taken on a specific date (based on schedule)
  const shouldTakeOnDate = useCallback((medication: Medication, date: Date): boolean => {
    const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()]

    // Match the vanilla version logic exactly
    // First check if medication has days property with day names (legacy format)
    if ((medication as any).days && Array.isArray((medication as any).days)) {
      return (medication as any).days.includes(dayName)
    }

    // Check if medication should be taken on this date based on daysOfWeek array (new format)
    if (medication.daysOfWeek && medication.daysOfWeek.length > 0) {
      return medication.daysOfWeek.includes(date.getDay())
    }

    // If no schedule is specified, include it (daily medication) - matching vanilla behavior
    return true
  }, [])

  const shouldTakeToday = useCallback((medication: Medication): boolean => {
    const today = new Date()
    const result = shouldTakeOnDate(medication, today)
    console.log(`🔍 shouldTakeToday [${medication.name}]:`, result)
    return result
  }, [shouldTakeOnDate])

  // Revised hasTimeTakenToday that uses the sync cache
  const hasTimeTakenToday = useCallback(async (medicationId: string, scheduledTime?: string): Promise<boolean> => {
    if (!user?.uid) return false

    // Filter local cache instead of making a new request
    const todayTakenActivities = todayActivities.filter(activity =>
      (activity.type === 'medication_taken' || activity.status === 'taken') &&
      activity.medicationId === medicationId
    )

    if (!scheduledTime || scheduledTime === '') {
      return todayTakenActivities.length > 0
    }

    // Check specific time
    return todayTakenActivities.some(activity => activity.scheduledTime === scheduledTime)
  }, [user?.uid, todayActivities])

  // Revised calculateTodayProgress that uses sync state
  const calculateTodayProgress = useCallback(async (): Promise<DailyProgress> => {
    if (!user?.uid) {
      return {
        date: new Date().toISOString().split('T')[0],
        totalMedications: 0,
        takenMedications: 0,
        missedMedications: 0,
        percentage: 0
      }
    }

    try {
      const today = new Date()

      // Filter medications that are active today (using isMedicationActiveOnDate for createdAt check)
      // and should be taken today (using shouldTakeToday which delegates to shouldTakeOnDate)
      const todayMedicationsList = medications.filter(med => 
        isMedicationActiveOnDate(med, today) && shouldTakeToday(med)
      )

      // Calculate progress by INDIVIDUAL TIMES, synchronously using cached activities
      let totalExpected = 0
      let totalTaken = 0

      for (const medication of todayMedicationsList) {
        const times = medication.times && Array.isArray(medication.times) ? medication.times : []

        if (times.length === 0) {
          // No specific times, count as 1 dose expected
          totalExpected += 1

          // Check if taken (synchronously)
          const isTaken = todayActivities.some(act =>
            (act.type === 'medication_taken' || act.status === 'taken') &&
            act.medicationId === medication.id
          )

          if (isTaken) totalTaken += 1
        } else {
          // Has specific times, count each time as 1 dose expected
          totalExpected += times.length
          for (const time of times) {
            // Check if taken (synchronously)
            const isTaken = todayActivities.some(act =>
              (act.type === 'medication_taken' || act.status === 'taken') &&
              act.medicationId === medication.id &&
              act.scheduledTime === time
            )

            if (isTaken) totalTaken += 1
          }
        }
      }

      const percentage = totalExpected > 0 ? Math.round((totalTaken / totalExpected) * 100) : 0

      const progressData: DailyProgress = {
        date: today.toISOString().split('T')[0],
        totalMedications: totalExpected,
        takenMedications: totalTaken,
        missedMedications: totalExpected - totalTaken,
        percentage
      }

      return progressData
    } catch (error) {
      console.error('❌ Error calculating progress:', error)
      return {
        date: new Date().toISOString().split('T')[0],
        totalMedications: 0,
        takenMedications: 0,
        missedMedications: 0,
        percentage: 0
      }
    }
  }, [user?.uid, medications, todayActivities, shouldTakeToday])

  // Load today's medications based on current medications
  const loadTodayMedications = useCallback(async () => {
    if (medications.length === 0) return

    try {
      const today = new Date()

      // Filter medications that are active today (using isMedicationActiveOnDate for createdAt check)
      // and should be taken today (using shouldTakeToday which delegates to shouldTakeOnDate)
      const todayMeds = medications.filter(med => 
        isMedicationActiveOnDate(med, today) && shouldTakeToday(med)
      )

      setTodayMedications(todayMeds)
      console.log('📅 Today medications loaded:', todayMeds.length)
    } catch (error) {
      console.error('❌ Error loading today\'s medications:', error)
    }
  }, [medications, isMedicationActiveOnDate, shouldTakeToday])

  const addMedication = useCallback(async (medicationData: Omit<Medication, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!user?.uid) throw new Error('User not authenticated')

    // Check subscription limits
    const maxMedications = getSubscriptionLimit(userProfile?.subscriptionPlan, 'maxMedications')
    if (medications.length >= maxMedications) {
      throw new Error(`Plan limit reached: You can only have ${maxMedications} medications on your current plan.`)
    }

    try {
      const medicationCollection = getMedicationCollection()
      const newMedication = {
        ...medicationData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }

      await addDoc(medicationCollection, newMedication)
      await loadMedications() // Reload to get updated list
      console.log('✅ Medication added successfully')
    } catch (error) {
      console.error('❌ Error adding medication:', error)
      throw error
    }
  }, [user?.uid, getMedicationCollection, loadMedications])

  const updateMedication = useCallback(async (id: string, medicationData: Partial<Medication>, options?: { silent?: boolean }) => {
    if (!user?.uid) throw new Error('User not authenticated')

    try {
      // PROACTIVELY find the medication to get its true path
      const existingMedication = medications.find(m => m.id === id)
      let medicationDoc

      if (existingMedication && existingMedication.collectionPath) {
        // Robust way: Use the path stored on the object itself
        console.log('Using explicit path for update:', existingMedication.collectionPath)
        medicationDoc = doc(db, existingMedication.collectionPath, id)
      } else {
        // Fallback to context-based logic (legacy)
        console.warn('⚠️ Usage of implicit path for update (fallback)')
        if (currentCase?.id) {
          medicationDoc = doc(db, `care-cases/${currentCase.id}/medications`, id)
        } else {
          medicationDoc = doc(db, `users/${user.uid}/medications`, id)
        }
      }

      // DEBUG: Verify document existence
      const docSnap = await getDoc(medicationDoc)
      if (!docSnap.exists()) {
        console.error('🔥 CRITICAL: Attempting to update non-existent document!', {
          path: medicationDoc.path,
          id: id,
          existingMedicationInState: existingMedication,
          currentCaseId: currentCase?.id,
          userUid: user.uid
        })
        throw new Error(`Document does not exist at ${medicationDoc.path}`)
      } else {
        console.log('✅ Document exists, proceeding with update:', medicationDoc.path)
      }

      await updateDoc(medicationDoc, {
        ...medicationData,
        updatedAt: serverTimestamp()
      })

      // Reload to ensure UI stays in sync
      const silent = typeof options?.silent === 'boolean' ? options.silent : false
      await loadMedications({ silent })
      console.log('✅ Medication updated successfully')
    } catch (error) {
      console.error('❌ Error updating medication:', error)
      throw error
    }
  }, [user?.uid, currentCase?.id, loadMedications, medications])

  const deleteMedication = useCallback(async (id: string) => {
    if (!user?.uid) throw new Error('User not authenticated')

    try {
      const existingMedication = medications.find(m => m.id === id)
      let medicationDoc

      if (existingMedication && existingMedication.collectionPath) {
        medicationDoc = doc(db, existingMedication.collectionPath, id)
      } else {
        if (currentCase?.id) {
          medicationDoc = doc(db, `care-cases/${currentCase.id}/medications`, id)
        } else {
          medicationDoc = doc(db, `users/${user.uid}/medications`, id)
        }
      }

      await deleteDoc(medicationDoc)

      await loadMedications() // Reload to get updated list
      console.log('✅ Medication deleted successfully')
    } catch (error) {
      console.error('❌ Error deleting medication:', error)
      throw error
    }
  }, [user?.uid, currentCase?.id, loadMedications, medications])

  const playTakeSound = useCallback(() => {
    if (!audioContext) return

    try {
      // Create a pleasant sound using oscillators (migrated from original)
      const oscillator1 = audioContext.createOscillator()
      const oscillator2 = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      // Configure frequencies for a harmonious sound
      oscillator1.frequency.setValueAtTime(523.25, audioContext.currentTime) // C5
      oscillator2.frequency.setValueAtTime(659.25, audioContext.currentTime) // E5

      // Configure wave type for smooth sound
      oscillator1.type = 'sine'
      oscillator2.type = 'sine'

      // Configure volume and fade out
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5)

      // Connect nodes
      oscillator1.connect(gainNode)
      oscillator2.connect(gainNode)
      gainNode.connect(audioContext.destination)

      // Play sound
      oscillator1.start(audioContext.currentTime)
      oscillator2.start(audioContext.currentTime)

      // Stop after 0.5 seconds
      oscillator1.stop(audioContext.currentTime + 0.5)
      oscillator2.stop(audioContext.currentTime + 0.5)

    } catch (e) {
      console.log('Error playing sound:', e)
    }
  }, [audioContext])

  const takeMedication = useCallback(async (medicationId: string, scheduledTime?: string) => {
    if (!user?.uid) throw new Error('User not authenticated')

    // Capture state for rollback
    const prevActivities = [...todayActivities]
    const prevMedications = [...medications]

    // Check for duplicates (Debounce/Idempotency)
    const isDuplicate = todayActivities.some(activity =>
      activity.medicationId === medicationId &&
      (
        (scheduledTime && activity.scheduledTime === scheduledTime) ||
        (!scheduledTime && (activity.type === 'medication_taken' || activity.status === 'taken'))
      )
    )

    if (isDuplicate) {
      console.warn('⚠️ Prevented duplicate takeMedication call', { medicationId, scheduledTime })
      return // Silently ignore to prevent duplication
    }

    try {
      // Find the medication
      const medication = medications.find(m => m.id === medicationId)
      if (!medication) throw new Error('Medication not found')

      // OPTIMISTIC UPDATE:
      // 1. Play sound immediately
      playTakeSound()

      // 2. Calculate new stock
      const currentStock = medication.currentStock || medication.stock || 0
      const newStock = Math.max(0, currentStock - 1)

      // 3. Create optimistic activity
      const optimisticActivity: MedicationActivity = {
        id: `temp_${Date.now()}`,
        medicationId,
        medicationName: medication.name,
        scheduledTime: scheduledTime || '',
        actualTime: new Date(),
        status: 'taken',
        type: 'medication_taken',
        timestamp: new Date(), // Local date object, matches Firestore Timestamp interface mostly
        notes: '',
        stockBefore: currentStock,
        stockAfter: newStock,
        createdAt: new Date()
      } as any // Cast to avoid strict Timestamp vs Date typing issues in local state

      // 4. Update local state immediately
      setTodayActivities(prev => [optimisticActivity, ...prev])

      setMedications(prev => prev.map(m => {
        if (m.id === medicationId) {
          return { ...m, stock: newStock, currentStock: newStock }
        }
        return m
      }))

      // ASYNC OPERATIONS:

      // Use case-specific history collection when available
      const historyCollection = getActivityCollection()

      // Create activity record for DB
      const dbActivity = {
        medicationId,
        medicationName: medication.name,
        scheduledTime: scheduledTime || '',
        actualTime: serverTimestamp(),
        status: 'taken',
        type: 'medication_taken',
        timestamp: serverTimestamp(),
        notes: '',
        stockBefore: currentStock,
        stockAfter: newStock,
        createdAt: serverTimestamp()
      }

      await addDoc(historyCollection, dbActivity)

      // Update medication stock in DB
      if (currentStock > 0) {
        // Build the update object from the medication to ensure we have the correct path helper or whatever
        // But here we can just use our internal updateMedication BUT pass a flag to NOT reload or reload silently
        // Since updateMedication calls loadMedications(), let's modify updateMedication too?
        // Or cleaner: just call updateDoc directly here to be specific.

        let medicationDoc
        if (medication.collectionPath) {
          medicationDoc = doc(db, medication.collectionPath, medicationId)
        } else {
          // Fallback logic SAME as updateMedication
          if (currentCase?.id) {
            medicationDoc = doc(db, `care-cases/${currentCase.id}/medications`, medicationId)
          } else {
            medicationDoc = doc(db, `users/${user.uid}/medications`, medicationId)
          }
        }

        await updateDoc(medicationDoc, {
          stock: newStock,
          currentStock: newStock,
          updatedAt: serverTimestamp()
        })
      }

      // Reload activities silently to get real IDs/timestamps
      await loadTodayActivities()

      // We don't need to reload medications fully because we updated stock locally
      // But to be super safe, we can do a silent reload
      // await loadMedications(true) 

      console.log('✅ Medication taken successfully (Optimistic)')
    } catch (error) {
      console.error('❌ Error taking medication:', error)
      // ROLLBACK on error
      setTodayActivities(prevActivities)
      setMedications(prevMedications)
      throw error
    }
  }, [user?.uid, medications, todayActivities, getActivityCollection, loadTodayActivities, playTakeSound, currentCase?.id])

  // New function to check stock before taking medication
  const checkStockBeforeTaking = useCallback(async (medicationId: string, _scheduledTime?: string) => {
    const medication = medications.find(m => m.id === medicationId)
    if (!medication) {
      return { shouldShowModal: false, medication: null }
    }

    const currentStock = medication.currentStock || medication.stock || 0
    const shouldShowModal = currentStock <= 0

    return { shouldShowModal, medication }
  }, [medications])

  // New function to take medication with optional stock update
  const takeMedicationWithStockUpdate = useCallback(async (medicationId: string, scheduledTime?: string, newStock?: number) => {
    if (!user?.uid) throw new Error('User not authenticated')

    try {
      // Find the medication
      const medication = medications.find(m => m.id === medicationId)
      if (!medication) throw new Error('Medication not found')

      // Update stock if provided
      if (newStock !== undefined) {
        await updateMedication(medicationId, {
          stock: newStock,
          currentStock: newStock
        })
      }

      // Now take the medication normally
      await takeMedication(medicationId, scheduledTime)

      console.log('✅ Medication taken with stock update successfully')
    } catch (error) {
      console.error('❌ Error taking medication with stock update:', error)
      throw error
    }
  }, [user?.uid, medications, updateMedication, takeMedication])

  const adjustStock = useCallback(async (medicationId: string, adjustment: number, reason = '', newMaxStock?: number) => {
    if (!user?.uid) throw new Error('User not authenticated')

    try {
      const medication = medications.find(m => m.id === medicationId)
      if (!medication) throw new Error('Medication not found')

      const newStock = (medication.stock || 0) + adjustment
      if (newStock < 0) throw new Error('Stock cannot be negative')

      // Use case-specific history collection when available
      const historyCollection = getActivityCollection()

      // Create activity record
      const activity = {
        medicationId,
        medicationName: medication.name,
        scheduledTime: '',
        actualTime: serverTimestamp(),
        status: 'stock_adjusted',
        type: 'stock_adjustment',
        timestamp: serverTimestamp(),
        notes: reason || `Stock adjusted: ${adjustment > 0 ? '+' : ''}${adjustment}`,
        stockBefore: medication.stock,
        stockAfter: newStock,
        createdAt: serverTimestamp()
      }

      await addDoc(historyCollection, activity)

      // Update medication stock and optionally maxStock
      const updateData: any = { stock: newStock }
      if (newMaxStock !== undefined) {
        updateData.maxStock = newMaxStock
      }

      await updateMedication(medicationId, updateData)

      // Reload activities
      await loadTodayActivities()

      console.log('✅ Stock adjusted successfully')
    } catch (error) {
      console.error('❌ Error adjusting stock:', error)
      throw error
    }
  }, [user?.uid, medications, updateMedication, getActivityCollection, loadTodayActivities])



  const deleteActivity = useCallback(async (activityId: string) => {
    if (!user?.uid) throw new Error('User not authenticated')

    // OPTIMISTIC UPDATE:
    // 1. Capture state for rollback
    const prevActivities = [...todayActivities]
    const prevMedications = [...medications]

    try {
      // 2. Find activity to delete (from local state to get stock info)
      const activityToDelete = todayActivities.find(a => a.id === activityId)

      if (!activityToDelete) {
        console.warn('Activity not found in local state during delete')
        return
      }

      // 3. Immediately remove from activities list (optimistic UI update)
      setTodayActivities(prev => prev.filter(a => a.id !== activityId))

      // 4. Optimistically restore stock if applicable
      let stockDiff = 0
      let medicationToUpdate: Medication | null = null
      
      if (
        (activityToDelete.type === 'medication_taken' || activityToDelete.status === 'taken') &&
        activityToDelete.stockBefore !== undefined &&
        activityToDelete.stockAfter !== undefined &&
        activityToDelete.stockBefore > activityToDelete.stockAfter
      ) {
        stockDiff = activityToDelete.stockBefore - activityToDelete.stockAfter
        medicationToUpdate = medications.find(m => m.id === activityToDelete.medicationId) || null

        if (medicationToUpdate) {
          // Update local state optimistically
          setMedications(prev => prev.map(m => {
            if (m.id === activityToDelete.medicationId) {
              return {
                ...m,
                stock: (m.stock || 0) + stockDiff,
                currentStock: (m.currentStock || 0) + stockDiff
              }
            }
            return m
          }))
        }
      }

      // ASYNC OPERATIONS:
      // 5. Delete activity from DB FIRST (before any reloads that might rehydrate it)
      const historyCollection = getActivityCollection()
      const activityRef = doc(historyCollection, activityId)

      // Verify activity exists before deleting
      const activitySnap = await getDoc(activityRef)
      if (!activitySnap.exists()) {
        console.warn('Activity not found in DB during delete, but was in local state')
        // Don't throw - just return since we already updated local state
        return
      }

      // Delete the activity document
      await deleteDoc(activityRef)

      // 6. Update medication stock directly (avoid updateMedication which triggers reloads)
      if (medicationToUpdate && stockDiff > 0) {
        let medicationDoc
        if (medicationToUpdate.collectionPath) {
          medicationDoc = doc(db, medicationToUpdate.collectionPath, medicationToUpdate.id)
        } else {
          // Fallback to context-based logic
          if (currentCase?.id) {
            medicationDoc = doc(db, `care-cases/${currentCase.id}/medications`, medicationToUpdate.id)
          } else {
            medicationDoc = doc(db, `users/${user.uid}/medications`, medicationToUpdate.id)
          }
        }

        await updateDoc(medicationDoc, {
          stock: (medicationToUpdate.stock || 0) + stockDiff,
          currentStock: (medicationToUpdate.currentStock || 0) + stockDiff,
          updatedAt: serverTimestamp()
        })
        console.log(`✅ Restored stock for medication ${medicationToUpdate.name}: +${stockDiff}`)
      }

      // 7. NO reload of activities (we already updated locally and deleted from DB)
      // NO reload of medications either (we updated locally and in DB directly)
      // This prevents race conditions and "ghosting" where activities reappear

      console.log('✅ Activity deleted successfully (Optimistic)')
    } catch (error) {
      console.error('❌ Error deleting activity:', error)
      // ROLLBACK on error
      setTodayActivities(prevActivities)
      setMedications(prevMedications)
      throw error
    }
  }, [user?.uid, currentCase?.id, getActivityCollection, medications, todayActivities])



  const getMonthlyHistory = useCallback(async (year: number, month: number): Promise<MedicationActivity[]> => {
    if (!user?.uid) return []

    try {
      const startOfMonth = new Date(year, month, 1)
      const endOfMonth = new Date(year, month + 1, 0, 23, 59, 59)

      console.log('🔍 Loading monthly history:', year, month, 'Range:', startOfMonth, 'to', endOfMonth)

      // Use case-specific history collection when available
      const historyCollection = getActivityCollection()
      console.log('📍 Using collection path:', historyCollection.path)

      // Use timestamp to match existing data format
      const historyQuery = query(
        historyCollection,
        where('timestamp', '>=', startOfMonth),
        where('timestamp', '<=', endOfMonth),
        orderBy('timestamp', 'desc')
      )

      const snapshot = await getDocs(historyQuery)
      console.log('📊 Monthly history query results:', snapshot.docs.length, 'documents')

      if (snapshot.docs.length > 0) {
        console.log('📄 Sample document:', snapshot.docs[0].data())
      }

      const activities: MedicationActivity[] = snapshot.docs.map(doc => {
        const data = doc.data()

        // FIXED: Handle both vanilla and React data formats
        const isVanillaFormat = data.type === 'medication_taken'
        const isReactFormat = data.status === 'taken'

        return {
          id: doc.id,
          medicationId: data.medicationId || '',
          medicationName: data.medicationName || '',
          scheduledTime: data.scheduledTime || '',
          // FIXED: Map both formats to React format
          status: isVanillaFormat ? 'taken' : (data.status || 'taken'),
          notes: data.notes || '',
          stockBefore: data.stockBefore,
          stockAfter: data.stockAfter,
          // FIXED: Use timestamp as primary field (vanilla format), fallback to actualTime (React format)
          actualTime: data.timestamp?.toDate() || data.actualTime?.toDate() || new Date(),
          createdAt: data.timestamp?.toDate() || data.createdAt?.toDate() || new Date()
        } as MedicationActivity
      })

      console.log('✅ Processed monthly activities:', activities.length)
      return activities
    } catch (error) {
      console.error('❌ Error getting monthly history:', error)
      return []
    }
  }, [user?.uid, getActivityCollection])

  const getDayActivities = useCallback(async (date: Date): Promise<MedicationActivity[]> => {
    if (!user?.uid) return []

    try {
      const startOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      const endOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1)

      console.log('🔍 Loading day activities for:', date.toDateString(), 'Range:', startOfDay, 'to', endOfDay)

      // Use case-specific history collection when available
      const historyCollection = getActivityCollection()
      console.log('📍 Using collection path:', historyCollection.path)

      // Use timestamp to match existing data format
      const dayQuery = query(
        historyCollection,
        where('timestamp', '>=', startOfDay),
        where('timestamp', '<', endOfDay),
        orderBy('timestamp', 'desc')
      )

      const snapshot = await getDocs(dayQuery)
      console.log('📊 Day activities query results:', snapshot.docs.length, 'documents')

      if (snapshot.docs.length > 0) {
        console.log('📄 Sample document:', snapshot.docs[0].data())
      }

      const activities: MedicationActivity[] = snapshot.docs.map(doc => {
        const data = doc.data()

        // FIXED: Handle both vanilla and React data formats
        const isVanillaFormat = data.type === 'medication_taken'
        const isReactFormat = data.status === 'taken'

        return {
          id: doc.id,
          medicationId: data.medicationId || '',
          medicationName: data.medicationName || '',
          scheduledTime: data.scheduledTime || '',
          // FIXED: Map both formats to React format
          status: isVanillaFormat ? 'taken' : (data.status || 'taken'),
          notes: data.notes || '',
          stockBefore: data.stockBefore,
          stockAfter: data.stockAfter,
          // FIXED: Use timestamp as primary field (vanilla format), fallback to actualTime (React format)
          actualTime: data.timestamp?.toDate() || data.actualTime?.toDate() || new Date(),
          createdAt: data.timestamp?.toDate() || data.createdAt?.toDate() || new Date()
        } as MedicationActivity
      })

      console.log('✅ Processed day activities:', activities.length)
      return activities
    } catch (error) {
      console.error('❌ Error getting day activities:', error)
      return []
    }
  }, [user?.uid, getActivityCollection])

  const getAdherenceStats = useCallback(async (startDate: Date, endDate: Date) => {
    if (!user?.uid) {
      return {
        totalScheduled: 0,
        totalTaken: 0,
        totalMissed: 0,
        adherencePercentage: 0,
        dailyStats: []
      }
    }

    try {
      // Use case-specific history collection when available
      const historyCollection = getActivityCollection()
      const statsQuery = query(
        historyCollection,
        where('timestamp', '>=', startDate),
        where('timestamp', '<=', endDate),
        orderBy('timestamp', 'asc')
      )

      const snapshot = await getDocs(statsQuery)
      const activities = snapshot.docs.map(doc => {
        const data = doc.data()

        // FIXED: Handle both vanilla and React data formats
        const isVanillaFormat = data.type === 'medication_taken'
        const isReactFormat = data.status === 'taken'

        return {
          id: doc.id,
          type: data.type || '',
          status: isVanillaFormat ? 'taken' : (data.status || ''),
          medicationId: data.medicationId || '',
          medicationName: data.medicationName || '',
          scheduledTime: data.scheduledTime || '',
          // FIXED: Use timestamp as primary field (vanilla format), fallback to actualTime (React format)
          timestamp: data.timestamp?.toDate() || new Date(),
          actualTime: data.timestamp?.toDate() || data.actualTime?.toDate() || new Date(),
          notes: data.notes || '',
          stockBefore: data.stockBefore,
          stockAfter: data.stockAfter
        }
      })

      console.log('📊 Raw activities loaded:', activities.length)

      // Calculate daily stats using existing historical data
      const dailyStats: Array<{ date: string; scheduled: number; taken: number; percentage: number }> = []
      const currentDate = new Date(startDate)

      while (currentDate <= endDate) {
        const dateString = currentDate.toISOString().split('T')[0]

        // Get activities for this specific day
        const dayActivities = activities.filter(activity => {
          const activityDate = activity.actualTime || activity.timestamp
          return activityDate.toISOString().split('T')[0] === dateString
        })

        // FIXED: Count taken medications from historical data (handle both formats)
        const takenActivities = dayActivities.filter(activity =>
          activity.type === 'medication_taken' ||
          activity.status === 'taken'
        )

        console.log(`📅 ${dateString}: ${takenActivities.length} taken from ${dayActivities.length} activities`)

        // Calculate scheduled medications for this day using current medication list
        // Filter by both active on date (createdAt check) and schedule (shouldTakeOnDate)
        const dayMeds = medications.filter(med => 
          isMedicationActiveOnDate(med, currentDate) && shouldTakeOnDate(med, currentDate)
        )
        let totalScheduled = 0

        for (const medication of dayMeds) {
          const times = medication.times || []
          totalScheduled += times.length === 0 ? 1 : times.length
        }

        const taken = takenActivities.length
        const percentage = totalScheduled === 0 ? 100 : (taken / totalScheduled) * 100

        dailyStats.push({
          date: dateString,
          scheduled: totalScheduled,
          taken,
          percentage
        })

        currentDate.setDate(currentDate.getDate() + 1)
      }

      const totalScheduled = dailyStats.reduce((sum, day) => sum + day.scheduled, 0)
      const totalTaken = dailyStats.reduce((sum, day) => sum + day.taken, 0)
      const totalMissed = totalScheduled - totalTaken
      const adherencePercentage = totalScheduled === 0 ? 100 : (totalTaken / totalScheduled) * 100

      console.log('📊 Final stats:', { totalScheduled, totalTaken, totalMissed, adherencePercentage })

      return {
        totalScheduled,
        totalTaken,
        totalMissed,
        adherencePercentage,
        dailyStats
      }
    } catch (error) {
      console.error('Error calculating adherence stats:', error)
      return {
        totalScheduled: 0,
        totalTaken: 0,
        totalMissed: 0,
        adherencePercentage: 0,
        dailyStats: []
      }
    }
  }, [user?.uid, getActivityCollection, medications, isMedicationActiveOnDate, shouldTakeOnDate])

  // Diagnostic function to check what's in the database
  const debugActivities = useCallback(async () => {
    if (!user?.uid) return

    try {
      const activityCollection = getActivityCollection()
      console.log('🔍 DEBUG: Collection path:', activityCollection.path)

      // Get last 10 documents without filters
      const debugQuery = query(activityCollection, orderBy('timestamp', 'desc'), limit(10))
      const snapshot = await getDocs(debugQuery)

      console.log('🔍 DEBUG: Found', snapshot.docs.length, 'total activities')
      snapshot.docs.forEach((doc, index) => {
        console.log(`📄 DEBUG Doc ${index}:`, doc.data())
      })
    } catch (error) {
      console.error('❌ DEBUG Error:', error)
    }
  }, [user?.uid, getActivityCollection])

  const getWeeklyStats = useCallback(async () => {
    if (!user?.uid) return []

    try {
      const weekAgo = new Date()
      weekAgo.setDate(weekAgo.getDate() - 7)

      const stats = await getAdherenceStats(weekAgo, new Date())

      return stats.dailyStats.map(day => ({
        day: new Date(day.date).toLocaleDateString('es-ES', { weekday: 'short' }),
        taken: day.taken,
        total: day.scheduled
      }))
    } catch (error) {
      console.error('Error getting weekly stats:', error)
      return []
    }
  }, [user?.uid, getAdherenceStats])

  const getMonthlyStats = useCallback(async () => {
    if (!user?.uid) return []

    try {
      const monthAgo = new Date()
      monthAgo.setMonth(monthAgo.getMonth() - 1)

      const stats = await getAdherenceStats(monthAgo, new Date())

      // Group by weeks
      const weeklyData: Record<string, { scheduled: number; taken: number }> = {}
      stats.dailyStats.forEach(day => {
        const date = new Date(day.date)
        const weekStart = new Date(date)
        weekStart.setDate(date.getDate() - date.getDay())
        const weekKey = weekStart.toISOString().split('T')[0]

        if (!weeklyData[weekKey]) {
          weeklyData[weekKey] = { scheduled: 0, taken: 0 }
        }
        weeklyData[weekKey].scheduled += day.scheduled
        weeklyData[weekKey].taken += day.taken
      })

      return Object.entries(weeklyData).map(([week, data]) => ({
        week: new Date(week).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }),
        adherence: data.scheduled === 0 ? 100 : (data.taken / data.scheduled) * 100
      }))
    } catch (error) {
      console.error('Error getting monthly stats:', error)
      return []
    }
  }, [user?.uid, getAdherenceStats])

  // Load medications when user or case changes
  useEffect(() => {
    if (user?.uid) {
      console.log('🔄 [useEffect 1] Case or user changed, reloading medications...', {
        userUid: user.uid,
        caseId: currentCase?.id,
        timestamp: new Date().toISOString()
      })

      // CRITICAL: Clear existing medications immediately when case changes to prevent showing wrong medications
      setMedications([])
      setTodayMedications([])
      setTodayActivities([]) // Clear activities cache too
      setProgress(null)

      // Debounce multiple calls with timeout
      const timeoutId = setTimeout(() => {
        loadMedications()
      }, 50)

      return () => clearTimeout(timeoutId)
    } else {
      // If no user, clear everything
      setMedications([])
      setTodayMedications([])
      setTodayActivities([])
      setProgress(null)
    }
  }, [user?.uid, currentCase?.id, loadMedications])

  // Load today's medications based on current medications
  useEffect(() => {
    if (medications.length > 0) {
      console.log('📅 [useEffect 2] Medications changed, loading today\'s medications...', {
        count: medications.length,
        timestamp: new Date().toISOString()
      })

      loadTodayMedications()
    }
  }, [medications, loadTodayMedications])

  // Update progress when medications or activities change
  useEffect(() => {
    if (medications.length > 0) {
      // Since calculateTodayProgress is now synchronous (using cached state),
      // we can just call it directly and it will be fast
      const updateProgress = async () => {
        const progressData = await calculateTodayProgress()
        setProgress(progressData)
      }

      updateProgress()
    }
  }, [medications, todayActivities, calculateTodayProgress])

  const value = {
    medications,
    todayMedications,
    todayActivities, // EXPOSED
    loading,
    progress,
    loadMedications,
    loadTodayMedications,
    addMedication,
    updateMedication,
    deleteMedication,
    takeMedication,
    takeMedicationWithStockUpdate,
    checkStockBeforeTaking,
    adjustStock,
    calculateTodayProgress,
    hasTimeTakenToday,
    shouldTakeToday,
    isMedicationActiveOnDate,
    shouldTakeOnDate,
    playTakeSound,
    getMonthlyHistory,
    getDayActivities,
    getAdherenceStats,
    getWeeklyStats,
    getMonthlyStats,
    deleteActivity,
    debugActivities
  }

  return (
    <MedicationContext.Provider value={value}>
      {children}
    </MedicationContext.Provider>
  )
} 